import type { NavSectionProps } from 'src/components/nav-section';

import { paths } from 'src/routes/paths';
import { CONFIG } from 'src/global-config';
import { SvgColor } from 'src/components/svg-color';
import { RoleType } from '../types/user';

const icon = (name: string) => (
  <SvgColor src={`${CONFIG.assetsDir}/assets/icons/navbar/${name}.svg`} />
);

// Le icone della home consulente stanno in assets/icons/db/, non in navbar/
const dbIcon = (name: string) => (
  <SvgColor src={`${CONFIG.assetsDir}/assets/icons/db/${name}.svg`} />
);

const ICONS = {
  user: icon('ic-user'),
  articoli: dbIcon('articoli'),
  crm: dbIcon('crm'),
  forecast: dbIcon('forecast'),
  businessTraining: dbIcon('business_training'),
  consulenti: icon('ic-banking'),
  chat: icon('ic-chat'),
  course: icon('ic-course'),
  dashboard: icon('ic-dashboard'),
  group: icon('ic-folder'),
  eventi: dbIcon('eventi'),
  analytics: icon('ic-analytics'),
  kanban: icon('ic-kanban'),
  calendar: icon('ic-calendar'),
  portafogliModello: icon('ic-file'),
};

// Il Beginner vede solo la sua vista: Panoramica, Documenti, Scheda, Esame OCF.
// Formazione: voce nascosta per ora (v. sections/vista-beginner/beginner-formazione-data.ts)
const navBeginner = (): NavSectionProps['data'] => [
  {
    subheader: 'Il tuo avviamento',
    items: [
      { title: 'Panoramica', path: paths.dashboard.beginner.root, icon: ICONS.dashboard },
      { title: 'Esame OCF', path: paths.dashboard.beginner.ocf, icon: ICONS.analytics },
      { title: 'Documenti', path: paths.dashboard.beginner.documenti, icon: ICONS.portafogliModello },
      { title: 'Scheda di inserimento', path: paths.dashboard.beginner.scheda, icon: ICONS.user },
      // { title: 'Formazione', path: paths.dashboard.beginner.formazione, icon: ICONS.course },
    ],
  },
];

const navBase = (
  role?: RoleType,
  strumentiRiservati: string[] = []
): NavSectionProps['data'] => [
  {
    subheader: 'Panoramica',
    items: [{ title: 'Home', path: paths.dashboard.root, icon: ICONS.dashboard }],
  },
  {
    subheader: 'Gestione',
    items: [
      {
        title: 'Clienti',
        path: paths.dashboard.cliente.root,
        icon: ICONS.user,
        allowedRoles: ['Amministratore', 'Backoffice'],
        children: [
          {
            title: 'Lista',
            path: paths.dashboard.cliente.root,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
          {
            title: 'Aggregazione',
            path: paths.dashboard.cliente.aggregation,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
          {
            title: 'Nuovo',
            path: paths.dashboard.cliente.new,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
          {
            title: 'Carica cliente',
            path: paths.dashboard.clienteScreen.root,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
          {
            title: 'Avatar',
            path: paths.dashboard.avatar.root,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
          {
            title: 'Mappa',
            path: paths.dashboard.mappaClienti.root,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
          {
            title: 'Campi Extra',
            path: paths.dashboard.cliente.campiExtraList,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
        ],
      },
      {
        title: 'Consulenti',
        path: paths.dashboard.consulente.root,
        icon: ICONS.consulenti,
        allowedRoles: ['Amministratore', 'Backoffice'],
        children: [
          {
            title: 'Lista',
            path: paths.dashboard.consulente.root,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
          {
            title: 'Nuovo',
            path: paths.dashboard.consulente.new,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
          {
            title: 'Permessi e gruppi',
            path: paths.dashboard.permessi.root,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
        ],
      },
      {
        title: 'Compilazioni',
        path: paths.dashboard.compilazione.root,
        icon: ICONS.course,
        allowedRoles: ['Amministratore', 'Backoffice', 'Consulente'],
        children: [
          {
            title: 'Lista',
            path: paths.dashboard.compilazione.root,
            allowedRoles: ['Amministratore', 'Backoffice', 'Consulente'],
          },
          {
            title: 'Nuovo',
            path: paths.dashboard.compilazione.new,
            allowedRoles: ['Amministratore', 'Backoffice', 'Consulente'],
          },
        ],
      },
      // Firma PDF: Amministratore e Backoffice, e il consulente a cui il server l'ha abilitata
      ...(role === 'Amministratore' ||
      role === 'Backoffice' ||
      strumentiRiservati.includes('firma-pdf')
        ? [
            {
              title: 'Firma PDF',
              path: paths.dashboard.firmaPdf.root,
              icon: ICONS.portafogliModello,
              allowedRoles: ['Amministratore', 'Backoffice', 'Consulente'] as RoleType[],
            },
          ]
        : []),
      // Carica cliente: per i consulenti abilitati dal server compare qui (per gli altri e' sotto Clienti)
      ...(role === 'Consulente' && strumentiRiservati.includes('cliente-screen')
        ? [
            {
              title: 'Carica cliente',
              path: paths.dashboard.clienteScreen.root,
              icon: ICONS.consulenti,
              allowedRoles: ['Consulente'] as RoleType[],
            },
          ]
        : []),
      {
        title: 'Articoli',
        path: paths.dashboard.articoli.root,
        icon: ICONS.articoli,
        allowedRoles: ['Amministratore', 'Backoffice', 'Consulente'],
      },
      {
        title: 'Corso CRM',
        path: paths.dashboard.corsoCrm.root,
        icon: ICONS.crm,
        allowedRoles: ['Amministratore', 'Backoffice', 'Consulente'],
      },
      // Amministratore e Backoffice trovano il Forecast sotto "Area Manager"; i consulenti (GM compresi) qui
      ...(role === 'Amministratore' || role === 'Backoffice'
        ? []
        : [
            {
              title: 'Forecast',
              path: paths.dashboard.forecast.root,
              icon: ICONS.forecast,
              allowedRoles: ['Amministratore', 'Backoffice', 'Consulente'] as RoleType[],
            },
          ]),
      {
        title: 'Business Training',
        path: paths.dashboard.businessTraining.root,
        icon: ICONS.businessTraining,
        allowedRoles: ['Amministratore', 'Backoffice', 'Consulente'],
      },
      {
        title: 'Eventi',
        path: paths.dashboard.eventi.root,
        icon: ICONS.eventi,
        allowedRoles: ['Amministratore', 'Backoffice', 'Consulente'],
      },
      {
        title: 'Gruppi',
        path: paths.dashboard.group.root,
        icon: ICONS.group,
        allowedRoles: ['Amministratore', 'Backoffice'],
        children: [
          {
            title: 'Lista',
            path: paths.dashboard.group.root,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
          {
            title: 'Nuovo',
            path: paths.dashboard.group.new,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
        ],
      },
      {
        title: 'Notifiche',
        path: paths.dashboard.notification.root,
        icon: ICONS.chat,
        allowedRoles: ['Amministratore', 'Backoffice'],
        children: [
          {
            title: 'Lista',
            path: paths.dashboard.notification.root,
            allowedRoles: ['Amministratore', 'Backoffice'],
          },
        ],
      },
    ],
  },
  // Statistiche: sezione propria, solo per chi la puo' usare
  ...(role === 'Amministratore' || role === 'Backoffice'
    ? [
        {
          subheader: 'Statistiche',
          items: [
            {
              title: 'Consulenti',
              path: paths.dashboard.statistiche.root,
              icon: ICONS.analytics,
              allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
            },
            {
              title: 'App',
              path: paths.dashboard.statisticheApp.root,
              icon: ICONS.analytics,
              allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
            },
          ],
        },
      ]
    : []),
  // sezione intera solo per chi la puo' usare: ai Consulenti non resta nemmeno il titolo vuoto
  ...(role === 'Amministratore' || role === 'Backoffice'
    ? [
        {
          subheader: 'Area Manager',
          items: [
            {
              title: 'Clienti diretti Banca',
              path: paths.dashboard.areaManager.assegnazione,
              icon: ICONS.consulenti,
              allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
              children: [
                {
                  title: 'Assegnazione',
                  path: paths.dashboard.areaManager.assegnazione,
                  allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
                },
                {
                  title: 'Distribuzione ed esito',
                  path: paths.dashboard.areaManager.distribuzione,
                  allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
                },
              ],
            },
            {
              title: 'Reclutamento',
              path: paths.dashboard.areaManager.reclutamento,
              icon: ICONS.user,
              allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
              children: [
                {
                  title: 'Dashboard',
                  path: paths.dashboard.areaManager.reclutamento,
                  allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
                },
                {
                  title: 'Elenco beginner',
                  path: paths.dashboard.beginnerGestione.root,
                  allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
                },
                {
                  title: 'Firma scheda idoneità',
                  path: paths.dashboard.areaManager.firmaScheda,
                  allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
                },
                {
                  title: 'Scheda inserimento',
                  path: paths.dashboard.areaManager.schedaBeginner,
                  allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
                },
              ],
            },
            {
              title: 'Firma accordi Team',
              path: paths.dashboard.areaManager.firmaTeam,
              icon: ICONS.portafogliModello,
              allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
            },
            {
              title: 'Forecast',
              path: paths.dashboard.forecast.root,
              icon: ICONS.forecast,
              allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
            },
          ],
        },
      ]
    : []),
  // Staff: plancia mattutina, solo Amministratore e Backoffice
  ...(role === 'Amministratore' || role === 'Backoffice'
    ? [
        {
          subheader: 'Staff',
          items: [
            {
              title: 'Plancia Cristian',
              path: paths.dashboard.staff.plancia,
              icon: ICONS.dashboard,
              allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
            },
            {
              title: 'Operatività Miche',
              path: paths.dashboard.staff.gantt,
              icon: ICONS.kanban,
              allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
            },
            {
              title: 'Plancia Francesco',
              path: paths.dashboard.staff.planciaFrancesco,
              icon: ICONS.analytics,
              allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
            },
            {
              title: 'Piano editoriale',
              path: paths.dashboard.staff.pianoEditoriale,
              icon: ICONS.calendar,
              allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
            },
          ],
        },
      ]
    : []),
  // Impostazioni: solo Amministratore e Backoffice, ai Consulenti non resta nemmeno il titolo
  ...(role === 'Amministratore' || role === 'Backoffice'
    ? [
        {
          subheader: 'Impostazioni',
          items: [
            {
              title: 'Portafogli Modello',
              path: paths.dashboard.portafogliModello.root,
              icon: ICONS.portafogliModello,
              allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
            },
          ],
        },
      ]
    : []),
];

// Voci governate dalle regole di visibilita' (chiave = risorsa nel backend, vedi utils/visibilita.ts).
// Per un Consulente compare solo cio' che il server dichiara visibile; il server rifiuta comunque le richieste.
const RISORSA_PER_VOCE: Record<string, string> = {
  Compilazioni: 'menu:compilazioni',
  Articoli: 'menu:articoli',
  'Corso CRM': 'menu:corso-crm',
  Eventi: 'menu:eventi',
  'Business Training': 'menu:business-training',
  Forecast: 'menu:forecast',
  'Firma PDF': 'menu:firma-pdf',
  'Carica cliente': 'menu:carica-cliente',
};

// Articoli, Corso CRM, Eventi e Business Training sono le voci pensate per i consulenti: Amministratore e Backoffice
// le trovano in una cartella "Materiali consulenti", i consulenti le vedono una per una (con le loro regole di visibilita').
const TITOLI_MATERIALI = ['Articoli', 'Corso CRM', 'Eventi', 'Business Training'];

const raggruppaMateriali = (data: NavSectionProps['data']): NavSectionProps['data'] =>
  data.map((sezione) => {
    const trovati = TITOLI_MATERIALI.map((t) => sezione.items.find((v) => v.title === t)).filter((v): v is (typeof sezione.items)[number] => !!v);
    if (trovati.length < 2) return sezione;
    const primo = sezione.items.findIndex((v) => TITOLI_MATERIALI.includes(v.title));
    const resto = sezione.items.filter((v) => !TITOLI_MATERIALI.includes(v.title));
    const cartella = {
      title: 'Materiali consulenti',
      path: trovati[0].path,
      icon: ICONS.group,
      allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[],
      children: trovati.map(({ title, path }) => ({ title, path, allowedRoles: ['Amministratore', 'Backoffice'] as RoleType[] })),
    };
    resto.splice(primo, 0, cartella);
    return { ...sezione, items: resto };
  });

export const getNavData = (
  role?: RoleType,
  strumentiRiservati: string[] = [],
  risorseVisibili: string[] = []
): NavSectionProps['data'] => {
  if (role === 'Beginner') return navBeginner();

  const data = navBase(role, strumentiRiservati);
  if (role !== 'Consulente') return raggruppaMateriali(data);

  return data
    .map((sezione) => ({
      ...sezione,
      items: sezione.items.filter((voce) => {
        const risorsa = RISORSA_PER_VOCE[voce.title];
        return !risorsa || risorseVisibili.includes(risorsa);
      }),
    }))
    .filter((sezione) => sezione.items.length > 0);
};
